import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReliabilityLine, SupplierReliability, reliabilityLines } from './supplier-reliability';

/**
 * Claude | 2026-10-23 | P7: a supplier's reliability for buyers - response, typical reply time, on-time delivery and
 * documents, each with its sample size. `compact` = one row of chips (cards); otherwise a small labelled grid (profile).
 * Renders nothing when there is no data object (e.g. a supplier viewing, or the call failed).
 */
@Component({
  selector: 'app-supplier-reliability',
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="srl" *ngIf="lines.length" [class.compact]="compact" role="list" aria-label="Supplier reliability">
      <div class="srl-item" role="listitem" *ngFor="let l of lines; trackBy: trackKey" [ngClass]="'tone-' + l.tone"
           [matTooltip]="l.tooltip" matTooltipPosition="above">
        <span class="srl-label">{{ l.label }}</span>
        <span class="srl-value">{{ l.value }}</span>
        <small class="srl-detail">{{ l.detail }}</small>
      </div>
    </div>
  `,
  styles: [`
    .srl { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 8px; margin: 8px 0; }
    .srl.compact { display: flex; flex-wrap: wrap; gap: 6px; margin: 6px 0 0; }
    .srl-item { display: flex; flex-direction: column; padding: 6px 10px; border-radius: 8px; background: #f3f6f9; border: 1px solid #e1e7ee; min-width: 0; }
    .srl.compact .srl-item { flex-direction: row; align-items: baseline; gap: 4px; padding: 2px 8px; border-radius: 999px; font-size: 12px; }
    .srl-label { font-size: 11px; color: #5b6b7b; text-transform: uppercase; letter-spacing: .02em; }
    .srl.compact .srl-label { text-transform: none; font-size: 12px; letter-spacing: 0; }
    .srl-value { font-weight: 600; color: #1d2b3a; }
    .srl-detail { color: #6b7a89; }
    .tone-none .srl-value { font-weight: 500; color: #6b7a89; }
    .tone-good { background: #eef8f3; border-color: #cde9da; }
  `],
})
export class SupplierReliabilityComponent {
  lines: ReliabilityLine[] = [];
  @Input() compact = false;
  private data: SupplierReliability | null | undefined = null;
  private extra = '';
  /** Appended to every tooltip (e.g. the service panel: the figures come from product RFQs and orders). */
  @Input() set note(v: string | null | undefined) { this.extra = v ? String(v) : ''; this.build(); }
  @Input() set reliability(r: SupplierReliability | null | undefined) { this.data = r; this.build(); }
  private build(): void {
    this.lines = reliabilityLines(this.data).map((l) => (this.extra ? { ...l, tooltip: l.tooltip + ' ' + this.extra } : l));
  }
  trackKey = (_: number, l: ReliabilityLine) => l.key;
}
